import React, { useState, useEffect } from 'react';
import logo from '../V Clinic logo - Blue gradient.PNG';

const DoctorDashboard = ({ onSelectPatient, onLogout }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    
    // 🛡️ REASON MODAL STATES
    const [showModal, setShowModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);

    const doctorName = localStorage.getItem('doctor_name');
    const doctorId = localStorage.getItem('doctor_id');
    const userBranch = localStorage.getItem('user_branch');

    // 🎯 MATCHED TO ODOO SELECTION LABELS
    const reasons = [
        "Complicated case",
        "Side effect",
        "Additional Note or Confirmation"
    ];

    // 🛰️ 1. FETCH TODAY'S QUEUE (Filtered by Doctor & Branch)
    useEffect(() => {
        const fetchQueue = async () => {
            setLoading(true);
            try {
                const response = await fetch(
                    `/api/appointments?branch=${encodeURIComponent(userBranch)}&doctorId=${doctorId}&date=${selectedDate}&role=doctor`
                );
                if (!response.ok) throw new Error('Odoo fetch failed');
                const data = await response.json();
                setAppointments(data); 
            } catch (err) {
                console.error("Dashboard error:", err);
            } finally {
                setLoading(false);
            }
        };
        if (doctorId && userBranch && selectedDate) fetchQueue();
    }, [doctorId, userBranch, selectedDate]); 

    // 🛡️ 2. THE LOGIC GATE
    const handleOpenWithReason = (p) => {
        if (p.status === 'GLOBAL RECORD' || p.status === 'OLD RECORD') {
            setSelectedPatient(p);
            setShowModal(true);
        } else {
            confirmOpen(p, "Normal Visit Today");
        }
    };

    // 📝 3. THE AUDIT LOG & TELEPORT BRIDGE
    const confirmOpen = (p, reason) => {
        const accessType = (p.status === 'GLOBAL RECORD' || p.status === 'OLD RECORD') 
            ? "Global Search" 
            : "Today Queue";

        fetch('/api/logs/access', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                hn: p.patient_hn,
                doctorId: doctorId,
                branchId: localStorage.getItem('user_branch_id'),
                type: accessType,
                reason: reason
            })
        }).catch(err => console.error("Log failed:", err));

        onSelectPatient({
            ...p,
            name: p.patient_name, 
            doctor: doctorName,
            branch: userBranch
        });
        
        setShowModal(false);
    };

    // 🕒 4. FILTER & SORT TODAY'S LIST
    const filteredAppointments = appointments
        .filter(appt => {
            const name = (appt.patient_name || "").toString().toLowerCase();
            const hn = (appt.patient_hn || "").toString().toLowerCase();
            return name.includes(searchTerm.toLowerCase()) || hn.includes(searchTerm.toLowerCase());
        })
        .sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00"));

    // --- CARD RENDERER ---
    const renderPatientCard = (p) => {
        let colors = { bg: '#F5F5F5', text: '#757575', border: '#E0E0E0' };
        const s = p.status;
        if (s === 'ลูกค้ามาถึง') colors = { bg: '#FFF9C4', text: '#FBC02D', border: '#FFF176' };
        else if (s === 'พบแพทย์') colors = { bg: '#E3F2FD', text: '#1976D2', border: '#90CAF9' };
        else if (s === 'ตรวจเสร็จ' || s === 'ชำระเงินแล้ว') colors = { bg: '#E8F5E9', text: '#2E7D32', border: '#A5D6A7' };
        else if (s === 'ไม่มาตามนัด') colors = { bg: '#FFEBEE', text: '#C62828', border: '#EF9A9A' };

        return (
            <div key={p.id || p.patient_hn} style={styles.patientCard}>
                <div style={styles.cardHeader}>
                    <span style={styles.timeTag}>{`🕒 ${p.time}`}</span>
                </div>
                <h3 style={styles.patientName}>{p.patient_name}</h3>
                <p style={styles.hnText}>HN: {p.patient_hn}</p>
                <div style={{ marginBottom: '15px' }}>
                    <span style={{ ...styles.statusTag, background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                        {p.status || 'รอ'}
                    </span>
                </div>
                <button 
                    onClick={() => handleOpenWithReason(p)} 
                    style={{ ...styles.openBtn, background: '#4CAF50' }}
                >
                    Open Patient OPD
                </button>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {/* --- HEADER --- */}
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <img src={logo} alt="Logo" style={{ width: '100px' }} />
                    <div>
                        <h1 style={{margin: 0}}>{doctorName}</h1>
                        <p style={{color: '#666'}}>สาขา {userBranch} | {new Date(selectedDate).toLocaleDateString('th-TH')}</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={styles.datePicker} />
                    <button onClick={onLogout} style={styles.logoutBtn}>Logout</button>
                </div>
            </div>

            {/* --- LOCAL SEARCH / FILTER ONLY --- */}
            <div style={styles.searchSection}>
                <input 
                    placeholder="Search today's queue..." 
                    style={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* --- TODAY LIST --- */}
            <h2 style={styles.sectionTitle}>Appointments List</h2>
            <div style={styles.queueGrid}>
                {loading ? <p>Loading Odoo...</p> : filteredAppointments.map(p => renderPatientCard(p))}
            </div>

            {/* 🚀 THE REASON MODAL UI */}
            {showModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3 style={{marginBottom: '10px'}}>Security Access Log</h3>
                        <p style={{marginBottom: '20px', color: '#666'}}>
                            Opening record for: <strong>{selectedPatient?.patient_name}</strong>. 
                            Please select a reason:
                        </p>
                        <div style={styles.reasonList}>
                            {reasons.map(r => (
                                <button key={r} onClick={() => confirmOpen(selectedPatient, r)} style={styles.reasonBtn}>
                                    {r}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setShowModal(false)} style={styles.cancelBtn}>Cancel / Go Back</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { padding: '40px', background: '#f4f7f9', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' },
    datePicker: { padding: '10px', borderRadius: '8px', border: '1px solid #ddd' },
    logoutBtn: { padding: '10px 20px', borderRadius: '8px', background: '#fff', cursor: 'pointer' },
    searchSection: { marginBottom: '30px' },
    searchInput: { width: '100%', padding: '20px', borderRadius: '15px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', fontSize: '18px', boxSizing: 'border-block' },
    sectionTitle: { fontSize: '22px', color: '#333', marginBottom: '20px' },
    queueGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
    patientCard: { background: '#fff', padding: '25px', borderRadius: '20px', boxShadow: '0 10px 20px rgba(0,0,0,0.05)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px' },
    timeTag: { background: '#e3f2fd', color: '#1976d2', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' },
    statusTag: { padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' },
    patientName: { margin: '0 0 5px 0', fontSize: '20px', color: '#333' },
    hnText: { color: '#888', margin: '0 0 5px 0', fontSize: '14px' },
    openBtn: { width: '100%', padding: '12px', borderRadius: '10px', border: 'none', color: '#fff', fontWeight: 'bold', cursor: 'pointer' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { background: '#fff', padding: '30px', borderRadius: '25px', width: '450px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    reasonList: { display: 'flex', flexDirection: 'column', gap: '12px', margin: '20px 0' },
    reasonBtn: { padding: '15px', borderRadius: '12px', border: '2px solid #eee', background: '#fcfcfc', cursor: 'pointer', fontWeight: 'bold', transition: '0.2s', fontSize: '14px' },
    cancelBtn: { color: '#ff5252', background: 'none', border: 'none', cursor: 'pointer', marginTop: '10px', fontWeight: 'bold' }
};

export default DoctorDashboard;